/* eslint-disable prettier/prettier */
import React, { useCallback, useEffect, useState, memo } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { useRoutes } from 'hooks';
import getAuthClient from 'clients/auth';
import CircularLoader from 'components/common/CircularLoader';
import ResetDocument from 'components/screens/Document/Reset';
import ResetODocument from 'components/screens/Document/ResetO';
import ErrorScreen from 'components/screens/Error';
import LessonContent from 'components/screens/LessonContent';
import LessonSummaries from 'components/screens/LessonSummaries';
import PatientDashboard from 'components/screens/PatientDashboard';
import 'styles/global.scss';
import { getConfig } from 'utils/config';
import { BUILD_NUMBER, VERSION_NUMBER } from 'utils/constants';
import Program from 'utils/program';
import { sessionSet, sessionClear, SessionKeys } from 'utils/tokenStorage';

/**
 * The main app component. The App component is memoized so it doesn't render
 * unless the props updates.
 */
const App = memo(() => {
  const routes = useRoutes();
  const [configLoaded, setConfigLoaded] = useState<boolean>();
  const [patientId, setPatientId] = useState<Nullable<string>>(null);
  const [error, setError] = useState<boolean>(false);

  const loadConfig = useCallback(async () => {
    await getConfig();
    setConfigLoaded(true);
  }, []);

  const initializePendo = useCallback(
    async (token: string, sessionId: string) => {
      // @ts-ignore
      if (typeof pendo === 'undefined') return;
      const offset = new Date().getTimezoneOffset();
      const authClient = await getAuthClient();
      const { uuid: id } = await authClient
        .getUserContext({
          userContextRequestDto: { token },
        })
        .then((res) => res.data);
      // @ts-ignore
      pendo.initialize({
        visitor: {
          // ID is typed as optional, so providing a fallback in case that comes in falsy.
          id,
          sessionId,
          staffId: id,
          timezoneOffset: `${offset}`,
        },
        account: {
          id: process.env.NODE_ENV,
          appVersion: VERSION_NUMBER,
        },
      });
    },
    [],
  );

  const onMessage = useCallback(
    async (event: any) => {
      try {
        const messageBody = event.data;
        if (messageBody.authToken !== undefined) {
          // We need to await the config when the event comes in
          // since it could happen prior to config loading
          const config = await getConfig();
          if (
            event.origin === config.PEARMD_WEB_URL ||
            event.origin === config.EHR_WEB_URL ||
            event.origin === config.APP_URL
          ) {
            // eslint-disable-next-line
            console.log(`Received message from ${event.origin}`);
            const { authToken, program, patientId, sessionId, userContext } =
              messageBody;
            sessionSet(SessionKeys.AUTH_TOKEN, authToken);
            sessionSet(SessionKeys.SESSION_ID, sessionId);
            initializePendo(authToken, sessionId);

            // So we'll save it in sessionStorage and use this value for all subsequent postMessages.
            sessionSet(SessionKeys.ORIGIN, event.origin);
            if (patientId && program) {
              // eslint-disable-next-line
              console.log(`Loading ${program} patient`);
              Program.set(program.toLowerCase());
              setPatientId(patientId);
              // We know we are using a valid origin, but we can't be sure which it is, EHR or PEARMD.
              // We will authenticate calls via user context if it exists (EHR).
              sessionSet(SessionKeys.USER_CONTEXT, userContext);
            }
          }
        }
      } catch (error) {
        setError(true);
      }
    },
    [initializePendo],
  );

  useEffect(() => {
    // eslint-disable-next-line
    console.log(`hylia-web version ${VERSION_NUMBER} (${BUILD_NUMBER})`);
    window.addEventListener('message', onMessage);
    return () => {
      sessionClear(SessionKeys.AUTH_TOKEN);
      sessionClear(SessionKeys.USER_CONTEXT);
      window.removeEventListener('message', onMessage);
    };
  }, [onMessage]);

  useEffect(() => {
    if (!configLoaded) {
      loadConfig();
    }
  }, [configLoaded, loadConfig]);

  if (!configLoaded && !patientId && !error) return <CircularLoader />;
  if (error) return <ErrorScreen />;
  return (
    <Switch>
      <Route exact path={routes.RESET_DOCUMENT} component={ResetDocument} />
      <Route exact path={routes.RESET_O_DOCUMENT} component={ResetODocument} />
      <Route
        exact
        path={routes.DOCUMENTS_LESSON_CONTENT}
        component={LessonContent}
      />
      <Route
        exact
        path={routes.DOCUMENTS_LESSON_SUMMARIES}
        component={LessonSummaries}
      />
      <Route
        path={[
          routes.PATIENT_DASHBOARD_OVERVIEW,
          routes.PATIENT_DASHBOARD_LESSONS,
          routes.PATIENT_DASHBOARD_LESSONS_CHART,
          routes.PATIENT_DASHBOARD_REWARDS,
          routes.PATIENT_DASHBOARD_CALENDAR,
          routes.PATIENT_DASHBOARD_CRAVINGS,
        ]}
        component={() => <PatientDashboard patientId={patientId} />}
      />
      <Redirect from="/" to={routes.PATIENT_DASHBOARD_OVERVIEW} />
      <Redirect
        from={routes.PATIENT_DASHBOARD}
        to={routes.PATIENT_DASHBOARD_OVERVIEW}
      />
    </Switch>
  );
});

App.displayName = 'App';

export default App;
